import { ICollectionResponse, IRequestParams, IResponse } from '@dash/apiInstance';
import { all, call, put, takeEvery } from 'redux-saga/effects';
import { sendRequest } from 'src/helpers/saga';
import { tableConfig } from 'src/settings';
import Api, { executeAction } from '../../../../../../../../helpers/api';
import { logger } from '../../../../../../../../helpers/logger';
import { t } from '../../../../../../../../i18n';
import notifications from '../../../../../../../../redux/notifications/actions';
import actionTypes from './actionTypes';
import actions from './actions';
import {
  LoadAgenciesRequestAction,
  LoadAllAvailableAgenciesRequestAction,
  ProcessActionRequestAction,
} from './actions.d';
import { IAgency, ILoadAgenciesPathParams, IProcessActionResponse } from './types.d';

const loadAgenciesAPI = new Api('bookings/:id/agencies_invites');
const loadAllAvailableAgenciesAPI = new Api('bookings/:id/suitable_agencies');

function* loadAgencies({ payload }: LoadAgenciesRequestAction<ILoadAgenciesPathParams, {}>) {
  try {
    const { page = 1, pageSize = tableConfig.pageSize, column, order, filter = '', id } = payload;
    const search = { ...payload, page, pageSize, column, order, filter };

    const result: ICollectionResponse<IAgency> = yield call<any, IRequestParams<ILoadAgenciesPathParams, {}>, string>(
      sendRequest,
      loadAgenciesAPI.load,
      { search, id },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200 && result?.data?.success?.data) {
      const { records, totalCount, filteredCount, sortableColumns } = result.data.success.data;

      yield put(
        actions.loadAgenciesSuccess({
          agencies: records,
          total: totalCount,
          filteredCount,
          sortableColumns,
        }),
      );
    } else {
      yield put(actions.loadAgenciesError());
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadAllAvailableAgencies({ payload }: LoadAllAvailableAgenciesRequestAction<ILoadAgenciesPathParams, {}>) {
  try {
    const { page = 1, pageSize = tableConfig.pageSize, column, order, filter = '', id } = payload;
    const search = { ...payload, page, pageSize, column, order, filter };

    const result: ICollectionResponse<IAgency> = yield call<any, IRequestParams<ILoadAgenciesPathParams, {}>, string>(
      sendRequest,
      loadAllAvailableAgenciesAPI.load,
      { search, id },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200 && result?.data?.success?.data) {
      const { records, totalCount, filteredCount, sortableColumns } = result.data.success.data;

      yield put(
        actions.loadAllAvailableAgenciesSuccess({
          agencies: records,
          total: totalCount,
          filteredCount,
          sortableColumns,
        }),
      );
    } else {
      yield put(actions.loadAgenciesError());
    }
  } catch (error) {
    logger.error(error);
  }
}

function* processAction({ payload }: ProcessActionRequestAction<{}, {}>) {
  try {
    const { url, method, bookingId, data } = payload;

    const result: IResponse<IProcessActionResponse> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      executeAction,
      { url, method, data },
      notifications.REQUEST_ERROR,
    );

    if (result?.messages || result?.success) {
      yield put({ type: actionTypes.LOAD_AGENCIES_REQUEST, payload: { id: bookingId } });
      yield put({ type: actionTypes.LOAD_ALL_AVAILABLE_AGENCIES_REQUEST, payload: { id: bookingId } });

      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: result?.messages?.join(' ') || '',
        }),
      );
    } else {
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.something_went_wrong'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(actionTypes.LOAD_AGENCIES_REQUEST, loadAgencies),
    takeEvery(actionTypes.LOAD_ALL_AVAILABLE_AGENCIES_REQUEST, loadAllAvailableAgencies),
    takeEvery(actionTypes.PROCESS_ACTION_REQUEST, processAction),
  ]);
}
